import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Check, RefreshCcw, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { LEGAL_NOTE } from "../data/brand";
import { ReviewCard } from "../components/Testimonials";
import { StockAlert } from "../components/StockAlert";
import { PublicReviewForm } from "../components/PublicReviewForm";
import { ProductGallery } from "../components/ProductGallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { id } = useParams();
  const { t, lang, addToCart, setCartOpen, stock, products, productsLoaded, track } = useStore();
  const navigate = useNavigate();
  const product = products.find((p) => p.id === id);
  const details = product?.details;
  const [mode, setMode] = useState("once");
  const [subBusy, setSubBusy] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/reviews`, { params: { product_id: id } }).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, [id]);

  if (!productsLoaded) return <div className="min-h-screen pt-40 text-center text-stone-500 font-mono text-sm">…</div>;
  if (!product || !details) return <Navigate to="/" replace />;

  const tp = t.productPage;
  const out = !product.buyable;
  const preorder = product.effective_status === "preorder";
  const subPrice = product.sub_price || Math.round(product.price * 0.9 * 100) / 100;
  const parseBenefit = (b) => { const [tt, ...rest] = String(b).split(" — "); return { t: tt, d: rest.join(" — ") }; };

  const handleAdd = () => {
    addToCart(product.id);
    toast.success(`${product.name[lang]} — ${t.products.added}`);
    setCartOpen(true);
  };

  const handleSubscribe = () => {
    setSubBusy(true);
    track("checkout_start", { subscription: product.id });
    navigate(`/checkout?subscribe=${product.id}`);
  };

  return (
    <div className="pt-28 lg:pt-32 pb-24 px-6 lg:px-14 xl:px-24 max-w-[1500px] mx-auto" data-testid={`product-page-${id}`}>
      <Link to="/#products" data-testid="back-to-collection" className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 hover:text-[#C85A32] transition-colors">
        <ArrowLeft size={13} /> {tp.back}
      </Link>

      <div className="mt-8 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:sticky lg:top-28 self-start"
        >
          <ProductGallery product={product} alt={product.name[lang]} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] mb-4" style={{ color: product.color }}>
            {product.tag[lang]} · {product.weight}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900">{product.name[lang]}</h1>
          <p className="mt-2 text-[12px] font-mono uppercase tracking-[0.2em] text-stone-500">{product.subtitle[lang]}</p>
          <p className="mt-6 text-base text-stone-600 leading-relaxed">{product.desc[lang]}</p>

          <p className="mt-5 text-sm italic font-serif text-stone-700 border-l-2 pl-4" style={{ borderColor: product.color }}>
            {details.taste[lang]}
          </p>

          <div className="mt-8 flex items-center gap-5">
            <span className="font-mono text-2xl text-stone-900" data-testid="product-page-price">
              {mode === "monthly" ? `${subPrice.toFixed(2)} CHF${t.sub.perMonth}` : `${product.price} CHF`}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-stone-400">{details.dosage[lang]}</span>
          </div>

          {preorder && (
            <div className="mt-6 rounded-2xl border border-[#4A624E]/40 bg-[#F4F7F4] px-6 py-4" data-testid="product-page-preorder">
              <p className="text-[12px] font-mono uppercase tracking-[0.2em] text-[#4A624E]">{t.products.preorder}</p>
              <p className="mt-1 text-sm text-stone-600">{product.preorder_note?.[lang] || t.products.preorderNote}</p>
            </div>
          )}
          {out ? (
            <div className="mt-6 rounded-2xl border border-[#C85A32]/40 bg-[#FDF6F0] px-6 py-5" data-testid="product-page-sold-out">
              <p className="text-[12px] font-mono uppercase tracking-[0.2em] text-[#C85A32]">{t.products.soldOut}</p>
              <p className="mt-1 text-sm text-stone-600">{t.stockAlert.title}</p>
              <div className="mt-4"><StockAlert productId={id} /></div>
            </div>
          ) : (
            <>
              {product.sub_price && (
                <div className="mt-6 flex rounded-full border border-stone-300 p-1 text-[11px] uppercase tracking-[0.12em] max-w-md" data-testid="purchase-mode-toggle">
                  <button
                    data-testid="mode-once"
                    onClick={() => setMode("once")}
                    className={`flex-1 py-2.5 px-3 rounded-full transition-all duration-300 ${mode === "once" ? "bg-stone-900 text-white" : "text-stone-500"}`}
                  >
                    {t.sub.once}
                  </button>
                  <button
                    data-testid="mode-monthly"
                    onClick={() => setMode("monthly")}
                    className={`flex-1 py-2.5 px-3 rounded-full transition-all duration-300 ${mode === "monthly" ? "bg-[#4A624E] text-white" : "text-stone-500"}`}
                  >
                    {t.sub.monthly}
                  </button>
                </div>
              )}
              {mode === "monthly" && product.sub_price ? (
                <div className="mt-5">
                  <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#4A624E]">{t.sub.save}</p>
                  <p className="mt-1 text-xs text-stone-500">{t.sub.note}</p>
                  <button
                    data-testid={`product-page-subscribe-${id}`}
                    onClick={handleSubscribe}
                    disabled={subBusy}
                    className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[#4A624E] text-white px-10 py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-[#364939] transition-colors duration-300 disabled:opacity-60"
                  >
                    <RefreshCcw size={15} /> {subBusy ? "..." : `${t.sub.cta} — ${subPrice.toFixed(2)} CHF${t.sub.perMonth}`}
                  </button>
                </div>
              ) : (
                <button
                  data-testid={`product-page-add-${id}`}
                  onClick={handleAdd}
                  className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-stone-900 text-[#FAF7F2] px-10 py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-[#C85A32] transition-colors duration-300"
                >
                  <Plus size={15} /> {t.products.add}
                </button>
              )}
            </>
          )}

          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-1" data-testid="product-trust">
            {t.trust.map((x) => <li key={x} className="flex items-center gap-1.5 text-[11px] text-stone-500"><ShieldCheck size={12} className="text-[#4A624E]" /> {x}</li>)}
          </ul>

          <div className="mt-12 space-y-10">
            <div data-testid="product-benefits">
              <h2 className="text-[11px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-5 flex items-center gap-3">
                <span className="w-6 h-px bg-stone-400" /> {tp.benefits}
              </h2>
              <div className="space-y-5">
                {details.benefits[lang].map(parseBenefit).map((b, i) => (
                  <div key={b.t} className="flex gap-4">
                    <span className="font-serif italic text-xl" style={{ color: product.color }}>0{i + 1}</span>
                    <div>
                      <h3 className="font-serif text-xl text-stone-900">{b.t}</h3>
                      <p className="mt-1 text-sm text-stone-600 leading-relaxed">{b.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="product-ingredients">
              <h2 className="text-[11px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-5 flex items-center gap-3">
                <span className="w-6 h-px bg-stone-400" /> {tp.ingredients}
              </h2>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {details.ingredients[lang].map((ing) => (
                  <li key={ing} className="flex items-start gap-2.5 text-sm text-stone-700">
                    <Check size={14} className="mt-0.5 shrink-0" style={{ color: product.color }} />
                    {ing}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[12px] text-stone-600 leading-relaxed" data-testid="ingredients-note">{tp.ingredientsNote}</p>
            </div>

            <div data-testid="product-preparation">
              <h2 className="text-[11px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-5 flex items-center gap-3">
                <span className="w-6 h-px bg-stone-400" /> {tp.preparation}
              </h2>
              <ol className="space-y-4">
                {details.preparation[lang].map((step, i) => (
                  <li key={step} className="flex gap-4 items-start border-t border-stone-200 pt-4">
                    <span className="font-mono text-[11px] tracking-[0.2em] text-stone-400 mt-1">0{i + 1}</span>
                    <p className="text-sm text-stone-700 leading-relaxed">{step}</p>
                  </li>
                ))}
              </ol>
            </div>

            <div data-testid="product-reviews">
              <h2 className="text-[11px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-5 flex items-center gap-3">
                <span className="w-6 h-px bg-stone-400" /> {tp.reviews} ({reviews.length})
              </h2>
              {reviews.length === 0 ? (
                <p className="text-sm text-stone-500 leading-relaxed" data-testid="product-no-reviews">{tp.noReviews}</p>
              ) : (
                <div className="grid gap-5">
                  {reviews.map((r, i) => <ReviewCard key={r.id} r={r} i={i} lang={lang} verifiedLabel={t.reviews.verified} />)}
                </div>
              )}
              <div className="mt-6"><PublicReviewForm productId={id} /></div>
            </div>

            <p className="text-[12px] text-stone-600 leading-relaxed border-t border-stone-200 pt-6" data-testid="product-legal-note">{LEGAL_NOTE[lang]}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
